import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import ApiMatches from '../api/ApiMatches'
import { track } from '../analytics'
import { services } from '../services'
import toastr from 'toastr'
import { queryKeys } from '../queryClient'
import { toUserFriendlyErrorMessage } from '../utils/userFacingError'

/**
 * Custom hook to handle match scheduling logic
 * Manages match data fetching, form submission, and navigation
 */
export function useMatchScheduler(matchId, leagueId) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [matchDatetime, setMatchDatetime] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [existingStatus, setExistingStatus] = useState('')
  const [homeCourt, setHomeCourt] = useState(null)
  const [awayCourt, setAwayCourt] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [matchData, setMatchData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Fetch match details and related data
  const fetchMatchDetails = useCallback(
    async (signal) => {
      try {
        setLoading(true)
        setError(null)
        const match = await ApiMatches.getMatch(matchId)

        if (signal?.aborted) return

        setMatchData(match)

        if (match.match_datetime) {
          // Format as yyyy-MM-ddTHH:mm for datetime-local input
          const dt = new Date(match.match_datetime)
          const iso = dt.toISOString()
          setMatchDatetime(iso.slice(0, 16))
        }
        setExistingStatus(match.status || '')

        // Load roster to map home courts
        try {
          const divResp = await services.league.getMyDivision(leagueId)

          if (signal?.aborted) return

          if (divResp.success) {
            const roster = divResp.data.roster || []
            const home = roster.find((p) => p.id === match.home_player_id)
            const away = roster.find((p) => p.id === match.away_player_id)
            setHomeCourt(home?.home_court || null)
            setAwayCourt(away?.home_court || null)

            // Update match data with opponent name for the deletion modal
            const currentUserPlayerId = divResp.data.player_id
            let opponentName = 'opponent'
            if (home && away) {
              if (currentUserPlayerId === match.home_player_id) {
                opponentName = `${away.first_name} ${away.last_name}`
              } else {
                opponentName = `${home.first_name} ${home.last_name}`
              }
            }
            setMatchData((prev) => ({
              ...prev,
              opponentName,
              division_id: divResp.data.division_id,
            }))
          }
        } catch (e) {
          // non-blocking error for roster loading
          console.error('Failed to load roster:', e)
        }
      } catch (err) {
        if (signal?.aborted) return
        const errorMessage = toUserFriendlyErrorMessage(
          err,
          'Failed to load match info',
        )
        setError(errorMessage)
        toastr.error(errorMessage)
        console.error(err)
      } finally {
        if (!signal?.aborted) {
          setLoading(false)
        }
      }
    },
    [matchId, leagueId],
  )

  useEffect(() => {
    const controller = new AbortController()
    fetchMatchDetails(controller.signal)
    return () => controller.abort()
  }, [fetchMatchDetails])

  // Handle match scheduling submission
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault()
      setSubmitting(true)
      try {
        await ApiMatches.scheduleMatch(matchId, matchDatetime)

        // Invalidate React Query caches after scheduling
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: queryKeys.scheduledMatches(),
          }),
          queryClient.invalidateQueries({ queryKey: queryKeys.nextMatch() }),
        ])

        // Track match scheduling
        try {
          track('match_scheduled', {
            match_id: matchId,
            league_id: leagueId,
            scheduled_datetime: matchDatetime,
          })
        } catch (analyticsErr) {
          console.error('Analytics tracking error:', analyticsErr)
        }

        toastr.success('Match scheduled!')

        // Show the availability deletion modal instead of navigating immediately
        if (matchData) {
          setShowDeleteModal(true)
        } else {
          navigate(`/leagues/${leagueId}`)
        }
      } catch (err) {
        toastr.error(
          toUserFriendlyErrorMessage(err, 'Failed to schedule match'),
        )
      } finally {
        setSubmitting(false)
      }
    },
    [matchId, leagueId, matchDatetime, matchData, navigate, queryClient],
  )

  // Handle delete modal close
  const handleDeleteModalClose = useCallback(() => {
    setShowDeleteModal(false)
    navigate(`/leagues/${leagueId}`)
  }, [leagueId, navigate])

  return {
    matchDatetime,
    setMatchDatetime,
    submitting,
    existingStatus,
    homeCourt,
    awayCourt,
    showDeleteModal,
    matchData,
    loading,
    error,
    handleSubmit,
    handleDeleteModalClose,
  }
}
